import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { X, ZoomIn, ZoomOut, RotateCcw, ImageOff, Trash2 } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { supabase } from '@/integrations/supabase/client';
import { extractPathFromUrl } from '@/lib/storage-utils';

type BucketName = 'patient-photos' | 'shift-photos' | 'avatars';

interface PhotoViewerModalProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  bucket?: BucketName;
  alt?: string;
  canDelete?: boolean;
  onDelete?: () => Promise<void>;
  isDeleting?: boolean;
}


export function PhotoViewerModal({
  open,
  onClose,
  imageUrl,
  bucket,
  alt = 'Foto',
  canDelete = false,
  onDelete,
  isDeleting = false
}: PhotoViewerModalProps) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTouchDistance = useRef<number | null>(null);

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 5;

  // Reset state and fetch signed URL when modal opens
  useEffect(() => {
    if (open) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsLoading(true);
      setError(false);

      if (bucket) {
        // Fetch signed URL for private buckets
        const fetchSignedUrl = async () => {
          try {
            const filePath = extractPathFromUrl(imageUrl, bucket);

            if (!filePath) {
              console.warn('Could not extract path from URL:', imageUrl);
              setError(true);
              setIsLoading(false);
              return;
            }

            const { data, error: urlError } = await supabase.storage
              .from(bucket)
              .createSignedUrl(filePath, 3600);

            if (urlError) {
              console.error('Error creating signed URL:', urlError);
              setError(true);
              setIsLoading(false);
              return;
            }

            setSignedUrl(data?.signedUrl || null);
            setIsLoading(false);
          } catch (err) {
            console.error('Error fetching signed URL:', err);
            setError(true);
            setIsLoading(false);
          }
        };

        fetchSignedUrl();
      } else {
        // For public URLs, use directly
        setSignedUrl(imageUrl);
        setIsLoading(false);
      }
    }
  }, [open, imageUrl, bucket]);

  const handleZoomIn = useCallback(() => {
    setScale((s) => Math.min(s * 1.5, MAX_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((s) => Math.max(s / 1.5, MIN_SCALE));
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s * delta)));
  }, []);

  // Mouse drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [scale, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch events for pinch zoom
  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      lastTouchDistance.current = getTouchDistance(e.touches);
    } else if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  }, [scale, position]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const distance = getTouchDistance(e.touches);
      if (distance && lastTouchDistance.current) {
        const delta = distance / lastTouchDistance.current;
        setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s * delta)));
        lastTouchDistance.current = distance;
      }
    } else if (e.touches.length === 1 && isDragging) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    lastTouchDistance.current = null;
  }, []);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 bg-black/95 border-none overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>Visualizar foto</DialogTitle>
        </VisuallyHidden>

        {/* Controls */}
        <div className="absolute top-2 right-2 z-50 flex gap-2">
          {canDelete && onDelete && (
            <Button
              size="icon"
              variant="secondary"
              className="rounded-full bg-destructive/80 hover:bg-destructive text-destructive-foreground"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <Button
            size="icon"
            variant="secondary"
            className="rounded-full bg-background/80 hover:bg-background"
            onClick={handleZoomOut}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="rounded-full bg-background/80 hover:bg-background"
            onClick={handleZoomIn}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="rounded-full bg-background/80 hover:bg-background"
            onClick={handleReset}
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="rounded-full bg-background/80 hover:bg-background"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Zoom level indicator */}
        <div className="absolute bottom-2 left-2 z-50 bg-background/80 text-foreground px-2 py-1 rounded text-sm">
          {Math.round(scale * 100)}%
        </div>

        {/* Image container */}
        <div
          ref={containerRef}
          className="w-[95vw] h-[95vh] flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {isLoading ? (
            <Skeleton className="w-64 h-64 rounded-lg" />
          ) : error || !signedUrl ? (
            <div className="flex flex-col items-center gap-4 text-muted-foreground">
              <ImageOff className="w-16 h-16" />
              <p>Não foi possível carregar a imagem</p>
            </div>
          ) : (
            <img
              src={signedUrl}
              alt={alt}
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              }}
              draggable={false}
            />
          )}
        </div>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir foto permanentemente?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">Esta ação é <strong>DEFINITIVA</strong> e não pode ser desfeita.</span>
              <span className="block text-destructive">A foto será removida permanentemente do sistema.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (onDelete) {
                  await onDelete();
                  setShowDeleteConfirm(false);
                  onClose();
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Excluir permanentemente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
